/**
 * DecisionReceipt — fail-closed receipt for a rejected admissibility decision.
 *
 * Single responsibility: render a receipt-style block that explains why a
 * transition was rejected, which predicate failed, and what to do next.
 * Domain-neutral: consumed by PDBG and any other governance UI that emits
 * structured rejection records.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, WARN } from "../design-system/tokens.js";
import { StatusBadge } from "./StatusBadge.js";

export interface DecisionReceiptProps {
  readonly receiptId: string;
  readonly reason: string;
  readonly violatedPredicate: string | null;
  readonly remediation?: string;
  readonly grade?: "A" | "B" | "C" | "F";
  readonly missingEvidence?: readonly string[];
  readonly createdAt: string;
}

const GRADE_TONE: Readonly<
  Record<NonNullable<DecisionReceiptProps["grade"]>, "success" | "info" | "warn" | "danger">
> = {
  A: "success",
  B: "success",
  C: "warn",
  F: "danger",
} as const;

/**
 * Renders a fail-closed decision receipt. Always indicates rejection; do not
 * use for admit results — pair with WitnessCard / ResultPanel for those.
 */
export const DecisionReceipt = React.memo(function DecisionReceipt({
  receiptId,
  reason,
  violatedPredicate,
  remediation,
  grade,
  missingEvidence,
  createdAt,
}: DecisionReceiptProps): React.JSX.Element {
  return (
    <aside
      aria-label={`Decision receipt ${receiptId}`}
      role="status"
      style={{
        padding: "0.75rem 1rem",
        border: `1px solid ${WARN[300]}`,
        backgroundColor: WARN[100],
        borderRadius: "0.5rem",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.5rem",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <StatusBadge label="REJECTED" tone="danger" />
          <span
            style={{
              fontFamily: FONT.mono,
              fontSize: "0.8125rem",
              color: WARN[700],
            }}
          >
            {receiptId}
          </span>
        </div>
        <time
          dateTime={createdAt}
          style={{
            color: GRAY[600],
            fontSize: "0.75rem",
            fontFamily: FONT.mono,
          }}
        >
          {createdAt}
        </time>
      </header>

      <p style={{ margin: "0.5rem 0 0", color: GRAY[900], fontSize: "0.875rem" }}>{reason}</p>

      {violatedPredicate !== null && (
        <p
          style={{
            margin: "0.25rem 0 0",
            color: GRAY[700],
            fontSize: "0.8125rem",
          }}
        >
          First-failing predicate:{" "}
          <code style={{ fontFamily: FONT.mono, color: GRAY[900] }}>{violatedPredicate}</code>
        </p>
      )}

      {missingEvidence !== undefined && missingEvidence.length > 0 && (
        <p
          style={{
            margin: "0.25rem 0 0",
            color: GRAY[700],
            fontSize: "0.8125rem",
          }}
        >
          Missing evidence:{" "}
          <code style={{ fontFamily: FONT.mono, color: GRAY[900] }}>
            {missingEvidence.join(", ")}
          </code>
        </p>
      )}

      {remediation !== undefined && (
        <p
          style={{
            margin: "0.5rem 0 0",
            padding: "0.5rem",
            color: GRAY[700],
            fontSize: "0.8125rem",
            backgroundColor: "white",
            border: `1px solid ${GRAY[300]}`,
            borderRadius: "0.25rem",
          }}
        >
          <strong>Remediation:</strong> {remediation}
        </p>
      )}

      {grade !== undefined && (
        <div style={{ marginTop: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ color: GRAY[600], fontSize: "0.75rem" }}>Replayability:</span>
          <StatusBadge label={`Grade ${grade}`} tone={GRADE_TONE[grade]} />
        </div>
      )}
    </aside>
  );
});
